import * as THREE from 'three';
import { PhysicsManager } from './physics';

export interface CameraConfig {
  fov: number;
  near: number;
  far: number;
  eyeHeight: number;
  thirdPersonDistance: number;
  collisionRadius: number;
  smoothing: number;
}

export class CameraController {
  private camera: THREE.PerspectiveCamera;
  private targetPosition: THREE.Vector3 = new THREE.Vector3();
  private targetRotation: THREE.Euler = new THREE.Euler(0, 0, 0, 'YXZ');
  private currentPosition: THREE.Vector3 = new THREE.Vector3();
  private isFirstPerson: boolean = true;
  private config: CameraConfig;
  private physicsManager: PhysicsManager | null = null;
  
  // Camera shake
  private shakeIntensity: number = 0;
  private shakeDecay: number = 0.95;
  private shakeOffset: THREE.Vector3 = new THREE.Vector3();
  
  // Camera bob (walking effect)
  private bobTime: number = 0;
  private bobIntensity: number = 0.03;
  private bobSpeed: number = 10;
  
  constructor(camera: THREE.PerspectiveCamera, config?: Partial<CameraConfig>) {
    this.camera = camera;
    this.config = {
      fov: 60,
      near: 0.1,
      far: 500,
      eyeHeight: 1.5,
      thirdPersonDistance: 5,
      collisionRadius: 0.2,
      smoothing: 0.15,
      ...config
    };
    
    // Initialize camera properties
    this.camera.fov = this.config.fov;
    this.camera.near = this.config.near;
    this.camera.far = this.config.far;
    this.camera.rotation.order = 'YXZ';
    this.camera.updateProjectionMatrix();
  }
  
  setPhysicsManager(physicsManager: PhysicsManager) {
    this.physicsManager = physicsManager;
  }
  
  /**
   * Update camera position and rotation
   * @param playerPosition - Player's world position
   * @param yaw - Horizontal rotation (radians)
   * @param pitch - Vertical rotation (radians)
   * @param deltaTime - Frame delta time
   * @param isMoving - Whether player is moving (for bobbing effect)
   */
  update(
    playerPosition: THREE.Vector3,
    yaw: number,
    pitch: number,
    deltaTime: number,
    isMoving: boolean = false
  ) {
    // Calculate target position based on view mode
    if (this.isFirstPerson) {
      this.targetPosition.copy(playerPosition);
      this.targetPosition.y += this.config.eyeHeight;
    } else {
      // Third person camera with collision detection
      this.calculateThirdPersonPosition(playerPosition, yaw, pitch);
    }
    
    // Add camera bob if moving (first person only)
    if (this.isFirstPerson && isMoving) {
      this.bobTime += deltaTime * this.bobSpeed;
      const bobX = Math.sin(this.bobTime) * this.bobIntensity;
      const bobY = Math.abs(Math.cos(this.bobTime * 2)) * this.bobIntensity * 0.5;
      this.targetPosition.x += bobX;
      this.targetPosition.y += bobY;
    }
    
    // Smooth camera movement
    const smoothing = 1.0 - Math.pow(1.0 - this.config.smoothing, deltaTime * 60);
    this.currentPosition.lerp(this.targetPosition, smoothing);
    
    // Apply shake if active
    if (this.shakeIntensity > 0.001) {
      this.shakeOffset.set(
        (Math.random() - 0.5) * this.shakeIntensity,
        (Math.random() - 0.5) * this.shakeIntensity,
        (Math.random() - 0.5) * this.shakeIntensity
      );
      this.shakeIntensity *= this.shakeDecay;
    } else {
      this.shakeOffset.set(0, 0, 0);
    }
    
    // Apply final position
    this.camera.position.copy(this.currentPosition).add(this.shakeOffset);
    
    // Apply rotation
    this.targetRotation.set(pitch, yaw, 0);
    this.camera.rotation.x = this.targetRotation.x;
    this.camera.rotation.y = this.targetRotation.y;
  }
  
  /**
   * Calculate third person camera position with collision detection
   */
  private calculateThirdPersonPosition(
    playerPosition: THREE.Vector3,
    yaw: number,
    pitch: number
  ) {
    // Start from player position at eye height
    const startPos = playerPosition.clone();
    startPos.y += this.config.eyeHeight;
    
    // Calculate desired camera offset
    const desiredOffset = new THREE.Vector3(
      Math.sin(yaw) * Math.cos(pitch) * this.config.thirdPersonDistance,
      Math.sin(pitch) * this.config.thirdPersonDistance,
      Math.cos(yaw) * Math.cos(pitch) * this.config.thirdPersonDistance
    );
    
    // Desired camera position
    const desiredPos = startPos.clone().add(desiredOffset);
    
    // Perform collision check if physics manager available
    if (this.physicsManager) {
      const collisionPos = this.checkCameraCollision(startPos, desiredPos);
      this.targetPosition.copy(collisionPos);
    } else {
      this.targetPosition.copy(desiredPos);
    }
  }
  
  /**
   * Check for camera collision between two points
   * Returns the closest valid camera position
   */
  private checkCameraCollision(from: THREE.Vector3, to: THREE.Vector3): THREE.Vector3 {
    // Simple raycast from player to camera position
    const direction = to.clone().sub(from).normalize();
    const distance = from.distanceTo(to);
    
    // Check multiple points along the ray for smooth collision
    const steps = 5;
    let validPosition = from.clone();
    
    for (let i = 1; i <= steps; i++) {
      const checkDist = (distance * i) / steps;
      const checkPos = from.clone().add(direction.clone().multiplyScalar(checkDist));
      
      // Check if this position is valid (not inside geometry)
      if (this.isPositionValid(checkPos)) {
        validPosition = checkPos;
      } else {
        break; // Stop at first collision
      }
    }
    
    // Add small offset to prevent clipping
    const pullBack = direction.clone().multiplyScalar(-this.config.collisionRadius);
    return validPosition.add(pullBack);
  }
  
  /**
   * Check if a position is valid (not colliding with world geometry)
   */
  private isPositionValid(_position: THREE.Vector3): boolean {
    // TODO: Implement proper collision check with physics world
    // For now, just return true
    return true;
  }
  
  /**
   * Apply camera shake effect (e.g., for damage or explosions)
   */
  shake(intensity: number) {
    this.shakeIntensity = Math.min(intensity, 1.0);
  }
  
  /**
   * Toggle between first and third person view
   */
  toggleViewMode() {
    this.isFirstPerson = !this.isFirstPerson;
    
    // Reset position for smooth transition
    if (this.isFirstPerson) {
      this.currentPosition.copy(this.targetPosition);
    }
  }
  
  /**
   * Set specific view mode
   */
  setViewMode(firstPerson: boolean) {
    this.isFirstPerson = firstPerson;
  }
  
  /**
   * Get current view mode
   */
  isFirstPersonMode(): boolean {
    return this.isFirstPerson;
  }
  
  /**
   * Set field of view (with smooth transition)
   */
  setFOV(fov: number, immediate: boolean = false) {
    if (immediate) {
      this.camera.fov = fov;
      this.camera.updateProjectionMatrix();
    } else {
      // Will be smoothed in update
      this.config.fov = fov;
    }
  }
  
  /**
   * Get the camera's forward direction
   */
  getForwardDirection(): THREE.Vector3 {
    const forward = new THREE.Vector3(0, 0, -1);
    forward.applyQuaternion(this.camera.quaternion);
    return forward;
  }
  
  /**
   * Get the camera's right direction
   */
  getRightDirection(): THREE.Vector3 {
    const right = new THREE.Vector3(1, 0, 0);
    right.applyQuaternion(this.camera.quaternion);
    return right;
  }
}
